import React, { useState, useEffect } from 'react';
import { Shield, Smartphone, Copy, Check, Loader2, Eye, EyeOff, AlertTriangle, CheckCircle, XCircle, MessageSquare, Phone, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTwoFactorStatus } from '../hooks/useData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Security = () => {
  const { t } = useTranslation();
  const { user, token, refreshUser } = useAuth();
  
  // Use cached 2FA status for instant loading
  const { twoFactorStatus, isLoading: loading, refresh: refreshStatus } = useTwoFactorStatus();
  
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedBackup, setCopiedBackup] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  
  // SMS 2FA states
  const [smsServiceAvailable, setSmsServiceAvailable] = useState(false);
  const [showSmsSetup, setShowSmsSetup] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [smsVerifying, setSmsVerifying] = useState(false);
  const [showSmsDisableModal, setShowSmsDisableModal] = useState(false);
  
  // Email OTP states
  const [emailOtpSending, setEmailOtpSending] = useState(false);
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpVerifying, setEmailOtpVerifying] = useState(false);
  const [showEmailDisableModal, setShowEmailDisableModal] = useState(false);

  useEffect(() => {
    checkSmsService();
  }, []);

  const checkSmsService = async () => {
    try {
      const response = await axios.get(`${API}/auth/sms/status`);
      setSmsServiceAvailable(response.data.configured);
    } catch (error) {
      console.error('Error checking SMS service:', error);
      setSmsServiceAvailable(false);
    }
  };

  const [setupLoading, setSetupLoading] = useState(false);

  const startSetup = async () => {
    setSetupLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSetupData(response.data);
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      toast.error(error.response?.data?.detail || 'Failed to start 2FA setup');
    } finally {
      setSetupLoading(false);
    }
  };

  const enableTwoFactor = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error(t('twoFactor.invalidCode'));
      return;
    }

    setVerifying(true);
    try {
      await axios.post(`${API}/auth/2fa/enable`, {
        code: verificationCode,
        secret: setupData.secret
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('twoFactor.enableSuccess'));
      setSetupData(null);
      setVerificationCode('');
      refreshStatus();
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      toast.error(error.response?.data?.detail || t('twoFactor.invalidCode'));
    } finally {
      setVerifying(false);
    }
  };

  const disableTwoFactor = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error(t('twoFactor.invalidCode'));
      return;
    }

    setVerifying(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, {
        code: disableCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('twoFactor.disableSuccess'));
      setShowDisableModal(false);
      setDisableCode('');
      refreshStatus();
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast.error(error.response?.data?.detail || t('twoFactor.invalidCode'));
    } finally {
      setVerifying(false);
    }
  };

  // SMS 2FA Functions
  const sendSmsCode = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error(t('sms2fa.invalidPhone'));
      return;
    }

    setSmsVerifying(true);
    try {
      await axios.post(`${API}/auth/sms/setup`, {
        phone_number: phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('sms2fa.codeSent'));
      setSmsSent(true);
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error(error.response?.data?.detail || t('sms2fa.sendFailed'));
    } finally {
      setSmsVerifying(false);
    }
  };

  const verifySmsCode = async () => {
    if (!smsCode || smsCode.length !== 6) {
      toast.error(t('twoFactor.invalidCode'));
      return;
    }

    setSmsVerifying(true);
    try {
      await axios.post(`${API}/auth/sms/verify`, {
        phone_number: phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`,
        code: smsCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('sms2fa.enableSuccess'));
      setShowSmsSetup(false);
      setPhoneNumber('');
      setSmsCode('');
      setSmsSent(false);
      refreshStatus();
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Error verifying SMS:', error);
      toast.error(error.response?.data?.detail || t('twoFactor.invalidCode'));
    } finally {
      setSmsVerifying(false);
    }
  };

  const disableSms2fa = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error(t('twoFactor.invalidCode'));
      return;
    }

    setVerifying(true);
    try {
      await axios.post(`${API}/auth/sms/disable`, {
        code: disableCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('sms2fa.disableSuccess'));
      setShowSmsDisableModal(false);
      setDisableCode('');
      refreshStatus();
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Error disabling SMS 2FA:', error);
      toast.error(error.response?.data?.detail || t('twoFactor.invalidCode'));
    } finally {
      setVerifying(false);
    }
  };

  const copyBackupCodes = () => {
    if (setupData?.backup_codes) {
      navigator.clipboard.writeText(setupData.backup_codes.join('\n'));
      setCopiedBackup(true);
      toast.success(t('common.copied'));
      setTimeout(() => setCopiedBackup(false), 3000);
    }
  };

  const copySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      toast.success(t('common.copied'));
    }
  };
  
  // Email OTP Functions
  const sendEmailOtp = async () => {
    setEmailOtpSending(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/email/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailOtpSent(true);
      toast.success(response.data.message || 'OTP sent to your email');
    } catch (error) {
      console.error('Error sending email OTP:', error);
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setEmailOtpSending(false);
    }
  };
  
  const verifyEmailOtp = async () => {
    if (!emailOtpCode || emailOtpCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setEmailOtpVerifying(true);
    try {
      await axios.post(`${API}/auth/2fa/email/verify?code=${emailOtpCode}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email OTP 2FA enabled!');
      setEmailOtpSent(false);
      setEmailOtpCode('');
      refreshStatus();
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Error verifying email OTP:', error);
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setEmailOtpVerifying(false);
    }
  };

  const disableEmailOtp = async () => {
    setEmailOtpVerifying(true);
    try {
      await axios.post(`${API}/auth/2fa/email/disable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email OTP disabled');
      setShowEmailDisableModal(false);
      refreshStatus();
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Error disabling email OTP:', error);
      toast.error(error.response?.data?.detail || 'Failed to disable');
    } finally {
      setEmailOtpVerifying(false);
    }
  };

  if (loading && !setupData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="security-page" className="min-h-screen py-6 px-3 md:px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">{t('twoFactor.title')}</h1>
          <p className="text-white/60 text-sm">{t('twoFactor.subtitle')}</p>
        </div>

        {/* Authenticator 2FA Status Card */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                twoFactorStatus?.enabled ? 'bg-green-500/20' : 'bg-yellow-500/20'
              }`}>
                <Smartphone className={`w-6 h-6 ${twoFactorStatus?.enabled ? 'text-green-500' : 'text-yellow-500'}`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {t('twoFactor.authenticator')} {twoFactorStatus?.enabled ? t('twoFactor.enabled') : t('twoFactor.disabled')}
                </h2>
                <p className="text-sm text-white/60">
                  {twoFactorStatus?.enabled 
                    ? t('twoFactor.authenticatorActive')
                    : t('twoFactor.authenticatorDesc')
                  }
                </p>
              </div>
            </div>
            <div className={`w-3 h-3 rounded-full ${twoFactorStatus?.enabled ? 'bg-green-500' : 'bg-yellow-500'}`} />
          </div>

          {!twoFactorStatus?.enabled && !setupData && (
            <button
              onClick={startSetup}
              data-testid="setup-2fa-btn"
              className="w-full bg-primary hover:bg-primary/90 text-white text-sm font-medium py-3 rounded-full transition-all hover:scale-105 active:scale-95"
            >
              {t('twoFactor.setup')}
            </button>
          )}

          {twoFactorStatus?.enabled && (
            <button
              onClick={() => setShowDisableModal(true)}
              className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm font-medium py-3 rounded-full transition-all border border-red-500/30"
            >
              {t('twoFactor.disable')}
            </button>
          )}
        </div>

        {/* SMS Backup 2FA Card */}
        {twoFactorStatus?.enabled && (
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-5 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  twoFactorStatus?.sms_enabled ? 'bg-green-500/20' : 'bg-blue-500/20'
                }`}>
                  <MessageSquare className={`w-6 h-6 ${twoFactorStatus?.sms_enabled ? 'text-green-500' : 'text-blue-500'}`} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">
                    {t('sms2fa.title')} {twoFactorStatus?.sms_enabled ? t('twoFactor.enabled') : ''}
                  </h2>
                  <p className="text-sm text-white/60">
                    {twoFactorStatus?.sms_enabled 
                      ? `${t('sms2fa.phoneLinked')}: ${twoFactorStatus.sms_phone}`
                      : t('sms2fa.description')
                    }
                  </p>
                </div>
              </div>
              {twoFactorStatus?.sms_enabled && (
                <div className="w-3 h-3 rounded-full bg-green-500" />
              )}
            </div>

            {!twoFactorStatus?.sms_enabled && !showSmsSetup && (
              <button
                onClick={() => setShowSmsSetup(true)}
                disabled={!smsServiceAvailable}
                data-testid="setup-sms-btn"
                className="w-full bg-blue-500 hover:bg-blue-500/90 text-white text-sm font-medium py-3 rounded-full transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {smsServiceAvailable ? t('sms2fa.setup') : t('sms2fa.unavailable')}
              </button>
            )}

            {twoFactorStatus?.sms_enabled && (
              <button
                onClick={() => setShowSmsDisableModal(true)}
                className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm font-medium py-3 rounded-full transition-all border border-red-500/30"
              >
                {t('sms2fa.disable')}
              </button>
            )}

            {/* SMS Setup Flow */}
            {showSmsSetup && !twoFactorStatus?.sms_enabled && (
              <div className="mt-4 pt-4 border-t border-white/10">
                {!smsSent ? (
                  <div className="space-y-3">
                    <label className="text-sm text-white/60 block">{t('sms2fa.enterPhone')}</label>
                    <div className="flex gap-2">
                      <div className="flex items-center bg-black/50 border border-white/10 rounded-lg px-3">
                        <Phone className="w-4 h-4 text-white/40" />
                      </div>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d+]/g, ''))}
                        placeholder="+1234567890"
                        data-testid="sms-phone-input"
                        className="flex-1 bg-black/50 border border-white/10 focus:border-primary/50 rounded-lg h-11 px-4 text-sm text-white placeholder:text-white/30 transition-all focus:ring-1 focus:ring-primary/50 focus:outline-none"
                      />
                    </div>
                    <p className="text-xs text-white/40">{t('sms2fa.phoneFormat')}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setShowSmsSetup(false);
                          setPhoneNumber('');
                        }}
                        className="flex-1 bg-white/5 hover:bg-white/10 text-white text-sm font-medium py-2.5 rounded-full transition-all border border-white/10"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        onClick={sendSmsCode}
                        disabled={smsVerifying || phoneNumber.length < 10}
                        className="flex-1 bg-blue-500 hover:bg-blue-500/90 text-white text-sm font-medium py-2.5 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {smsVerifying ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('sms2fa.sendCode')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <label className="text-sm text-white/60 block">{t('sms2fa.enterCode')}</label>
                    <input
                      type="text"
                      value={smsCode}
                      onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      data-testid="sms-code-input"
                      className="w-full bg-black/50 border border-white/10 focus:border-primary/50 rounded-lg h-12 px-4 text-center text-2xl font-mono text-white tracking-widest placeholder:text-white/20 transition-all focus:ring-1 focus:ring-primary/50 focus:outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSmsSent(false);
                          setSmsCode('');
                        }}
                        className="flex-1 bg-white/5 hover:bg-white/10 text-white text-sm font-medium py-2.5 rounded-full transition-all border border-white/10"
                      >
                        {t('common.back')}
                      </button>
                      <button
                        onClick={verifySmsCode}
                        disabled={smsVerifying || smsCode.length !== 6}
                        className="flex-1 bg-blue-500 hover:bg-blue-500/90 text-white text-sm font-medium py-2.5 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {smsVerifying ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('twoFactor.verifyCode')}
                      </button>
                    </div>
                    <button
                      onClick={sendSmsCode}
                      disabled={smsVerifying}
                      className="w-full text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      {t('sms2fa.resendCode')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Email OTP Card */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                twoFactorStatus?.email_otp_enabled ? 'bg-green-500/20' : 'bg-purple-500/20'
              }`}>
                <Mail className={`w-6 h-6 ${twoFactorStatus?.email_otp_enabled ? 'text-green-500' : 'text-purple-500'}`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Email OTP {twoFactorStatus?.email_otp_enabled ? 'Enabled' : 'Available'}
                </h2>
                <p className="text-sm text-white/60">
                  {twoFactorStatus?.email_otp_enabled 
                    ? `OTP will be sent to ${user?.email}`
                    : 'Receive one-time codes via email'
                  }
                </p>
              </div>
            </div>
            <div className={`w-3 h-3 rounded-full ${twoFactorStatus?.email_otp_enabled ? 'bg-green-500' : 'bg-purple-500'}`} />
          </div>

          {!twoFactorStatus?.email_otp_enabled && !emailOtpSent && (
            <button
              onClick={sendEmailOtp}
              disabled={emailOtpSending}
              data-testid="setup-email-otp-btn"
              className="w-full bg-purple-500 hover:bg-purple-500/90 text-white text-sm font-medium py-3 rounded-full transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
            >
              {emailOtpSending ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </span>
              ) : (
                'Enable Email OTP'
              )}
            </button>
          )}

          {emailOtpSent && !twoFactorStatus?.email_otp_enabled && (
            <div className="space-y-3">
              <p className="text-sm text-green-400 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                OTP sent to {user?.email}
              </p>
              <label className="text-sm text-white/60 block">Enter the 6-digit code:</label>
              <input
                type="text"
                value={emailOtpCode}
                onChange={(e) => setEmailOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                data-testid="email-otp-code-input"
                className="w-full bg-black/50 border border-white/10 focus:border-purple-500/50 rounded-lg h-12 px-4 text-center text-2xl font-mono text-white tracking-widest placeholder:text-white/20 transition-all focus:ring-1 focus:ring-purple-500/50 focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEmailOtpSent(false);
                    setEmailOtpCode('');
                  }}
                  className="flex-1 bg-white/5 hover:bg-white/10 text-white text-sm font-medium py-2.5 rounded-full transition-all border border-white/10"
                >
                  Cancel
                </button>
                <button
                  onClick={verifyEmailOtp}
                  disabled={emailOtpVerifying || emailOtpCode.length !== 6}
                  data-testid="verify-email-otp-btn"
                  className="flex-1 bg-purple-500 hover:bg-purple-500/90 text-white text-sm font-medium py-2.5 rounded-full transition-all disabled:opacity-50"
                >
                  {emailOtpVerifying ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Verify'}
                </button>
              </div>
              <button
                onClick={sendEmailOtp}
                disabled={emailOtpSending}
                className="w-full text-sm text-purple-400 hover:text-purple-300 transition-colors"
              >
                Resend Code
              </button>
            </div>
          )}

          {twoFactorStatus?.email_otp_enabled && (
            <>
              <div className="flex items-center gap-2 text-green-400 text-sm mb-3">
                <CheckCircle className="w-4 h-4" />
                Email OTP is active for your account
              </div>
              <button
                onClick={() => setShowEmailDisableModal(true)}
                className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm font-medium py-3 rounded-full transition-all border border-red-500/30"
              >
                Disable Email OTP
              </button>
            </>
          )}
        </div>

        {/* Authenticator Setup Flow */}
        {setupData && (
          <div className="space-y-4">
            {/* QR Code */}
            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Smartphone className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold text-white">{t('twoFactor.setupTitle')}</h3>
              </div>
              <p className="text-sm text-white/60 mb-4">{t('twoFactor.setupDesc')}</p>

              <div className="flex justify-center mb-4">
                <div className="bg-white p-3 rounded-xl">
                  <img 
                    src={setupData.qr_code} 
                    alt="2FA QR Code" 
                    className="w-48 h-48"
                    data-testid="2fa-qr-code"
                  />
                </div>
              </div>

              {/* Manual Entry */}
              <div className="bg-white/5 rounded-lg p-3 mb-4">
                <p className="text-xs text-white/60 mb-1">{t('twoFactor.cantScanQR')}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm text-primary font-mono break-all">{setupData.secret}</code>
                  <button
                    onClick={copySecret}
                    className="p-2 text-white/60 hover:text-white transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Verification Input */}
              <div className="mb-4">
                <label className="text-sm text-white/60 mb-2 block">{t('twoFactor.enterCode')}</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  data-testid="2fa-verification-input"
                  className="w-full bg-black/50 border border-white/10 focus:border-primary/50 rounded-lg h-12 px-4 text-center text-2xl font-mono text-white tracking-widest placeholder:text-white/20 transition-all focus:ring-1 focus:ring-primary/50 focus:outline-none"
                />
              </div>

              <button
                onClick={enableTwoFactor}
                disabled={verifying || verificationCode.length !== 6}
                data-testid="enable-2fa-btn"
                className="w-full bg-primary hover:bg-primary/90 text-white text-sm font-medium py-3 rounded-full transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {verifying ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('twoFactor.verifying')}
                  </span>
                ) : (
                  t('twoFactor.verifyCode')
                )}
              </button>
            </div>

            {/* Backup Codes */}
            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  <h3 className="text-lg font-semibold text-white">{t('twoFactor.backupCodes')}</h3>
                </div>
                <button
                  onClick={() => setShowBackupCodes(!showBackupCodes)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  {showBackupCodes ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-sm text-white/60 mb-4">{t('twoFactor.backupCodesDesc')}</p>

              {showBackupCodes && (
                <>
                  <div className="bg-black/50 rounded-lg p-4 mb-3 grid grid-cols-2 gap-2">
                    {setupData.backup_codes.map((code, index) => (
                      <code key={index} className="text-sm font-mono text-white/90">{code}</code>
                    ))}
                  </div>
                  <button
                    onClick={copyBackupCodes}
                    className="w-full bg-white/5 hover:bg-white/10 text-white text-sm font-medium py-2.5 rounded-full transition-all border border-white/10 flex items-center justify-center gap-2"
                  >
                    {copiedBackup ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    {t('twoFactor.copyBackupCodes')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Security Notice */}
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 mt-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-white mb-1">{t('twoFactor.securityNotice')}</h4>
              <p className="text-xs text-white/60">
                {t('twoFactor.securityNoticeDesc')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Disable Authenticator 2FA Modal */}
      {showDisableModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black/90 border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{t('twoFactor.disable')}</h3>
                <p className="text-sm text-white/60">{t('twoFactor.disableConfirm')}</p>
              </div>
            </div>

            <input
              type="text"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              className="w-full bg-black/50 border border-white/10 focus:border-red-500/50 rounded-lg h-12 px-4 text-center text-2xl font-mono text-white tracking-widest placeholder:text-white/20 transition-all focus:ring-1 focus:ring-red-500/50 focus:outline-none mb-4"
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDisableModal(false);
                  setDisableCode('');
                }}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white text-sm font-medium py-3 rounded-full transition-all border border-white/10"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={disableTwoFactor}
                disabled={verifying || disableCode.length !== 6}
                className="flex-1 bg-red-500 hover:bg-red-500/90 text-white text-sm font-medium py-3 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable SMS 2FA Modal */}
      {showSmsDisableModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black/90 border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{t('sms2fa.disableTitle')}</h3>
                <p className="text-sm text-white/60">{t('sms2fa.disableConfirm')}</p>
              </div>
            </div>

            <p className="text-xs text-white/40 mb-3">{t('sms2fa.enterAuthCode')}</p>
            <input
              type="text"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              className="w-full bg-black/50 border border-white/10 focus:border-red-500/50 rounded-lg h-12 px-4 text-center text-2xl font-mono text-white tracking-widest placeholder:text-white/20 transition-all focus:ring-1 focus:ring-red-500/50 focus:outline-none mb-4"
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSmsDisableModal(false);
                  setDisableCode('');
                }}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white text-sm font-medium py-3 rounded-full transition-all border border-white/10"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={disableSms2fa}
                disabled={verifying || disableCode.length !== 6}
                className="flex-1 bg-red-500 hover:bg-red-500/90 text-white text-sm font-medium py-3 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable Email OTP Modal */}
      {showEmailDisableModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black/90 border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Disable Email OTP</h3>
                <p className="text-sm text-white/60">Are you sure you want to disable Email OTP?</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowEmailDisableModal(false)}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white text-sm font-medium py-3 rounded-full transition-all border border-white/10"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={disableEmailOtp}
                disabled={emailOtpVerifying}
                className="flex-1 bg-red-500 hover:bg-red-500/90 text-white text-sm font-medium py-3 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {emailOtpVerifying ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Security;
